import React, { useState } from 'react';
import api from '../services/api';
import { trackEvent } from '../utils/analytics';
import { usePreferences } from '../contexts/PreferencesContext';
import { useTranslation } from '../contexts/LanguageContext';
import { isNative } from '../utils/native';
import { storeUrlForThisDevice } from '../services/storeReview';
import './FeedbackPrompt.css';

const MOODS = [
    { value: 1, emoji: '😕', label: 'feedback.moods.notGreat' },
    { value: 2, emoji: '🙂', label: 'feedback.moods.okay' },
    { value: 3, emoji: '😍', label: 'feedback.moods.lovedIt' },
];

/**
 * One-tap mood + optional text. Used inline on the victory overlay after a
 * few wins, and as a modal from the Options menu ("Send feedback").
 */
export default function FeedbackPrompt({ context = 'victory', title, mode, difficulty, gameId, gamesCompleted, lang = 'en', onDone, compact = false }) {
    const { updatePreference } = usePreferences();
    const { t } = useTranslation();
    const [rating, setRating] = useState(null);
    const [message, setMessage] = useState('');
    const [sent, setSent] = useState(false);
    const [sending, setSending] = useState(false);

    const send = async (finalRating, finalMessage) => {
        if (sending) return;
        setSending(true);
        try {
            await api.sendFeedback({ rating: finalRating, message: finalMessage, context, mode, difficulty, gameId, gamesCompleted, lang });
            trackEvent('Feedback Sent', { rating: String(finalRating ?? ''), has_message: String(!!finalMessage), context });
        } catch { /* best-effort */ }
        if (finalRating) updatePreference('feedbackRatedAt', new Date().toISOString());
        setSending(false);
        setSent(true);
        // Happy web players on a phone get a way to install the app, so the thanks stays up longer.
        setTimeout(() => onDone?.(), appUrl && finalRating === 3 ? 8000 : 1600);
    };

    const appUrl = !isNative() ? storeUrlForThisDevice() : null;

    if (sent) {
        return (
            <div role="status">
                <p className="fb-thanks">{t('feedback.thanks')}</p>
                {appUrl && rating === 3 && (
                    <a className="fb-app-link" href={appUrl} target="_blank" rel="noopener noreferrer"
                        onClick={() => trackEvent('Get App Clicked', { context: `feedback_${context}` })}>
                        {t('feedback.getApp')}
                    </a>
                )}
            </div>
        );
    }

    const question = rating === 3 ? t('feedback.likeMost') : t('feedback.improve');

    return (
        <div className={`fb-prompt${compact ? ' fb-compact' : ''}`} role="group" aria-label={t('feedback.groupLabel')}>
            {rating === null ? (
                <>
                    <p className="fb-title">{title || (context === 'menu' ? t('feedback.titleMenu') : t('feedback.titleVictory'))}</p>
                    <div className="fb-moods">
                        {MOODS.map(m => (
                            <button key={m.value} type="button" className="fb-mood" onClick={() => setRating(m.value)} aria-label={t(m.label)}>
                                <span aria-hidden="true">{m.emoji}</span>
                                <span className="fb-mood-label">{t(m.label)}</span>
                            </button>
                        ))}
                    </div>
                    {onDone && <button type="button" className="fb-skip" onClick={() => onDone('skipped')}>{t('feedback.notNow')}</button>}
                </>
            ) : (
                <>
                    <label className="fb-title" htmlFor="fb-message">{question} <span className="fb-optional">{t('feedback.optional')}</span></label>
                    <textarea id="fb-message" className="fb-text" rows={compact ? 2 : 3} maxLength={1000} value={message}
                        onChange={e => setMessage(e.target.value)} placeholder={t('feedback.placeholder')} autoFocus />
                    <div className="fb-actions">
                        <button type="button" className="fb-send" disabled={sending} onClick={() => send(rating, message.trim())}>
                            {sending ? t('feedback.sending') : t('feedback.send')}
                        </button>
                        <button type="button" className="fb-skip" onClick={() => send(rating, '')}>{t('feedback.justRating')}</button>
                    </div>
                </>
            )}
        </div>
    );
}
